import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { UseAuth, UseUserDetails, UseIsDemo, UseIsAdmin } from "./auth";
import { types } from "../redux/kpi/types";
import { storeMyKpis } from '../services/kpiStorage';
import { saveOfflineKpi, deletePendingKpi } from '../services/offlineStorage';
import { useOnlineStatus } from "./useNetwork";
import { generateUuid } from "../utils/generateUuid";
import {
  filterKpisForPublicDisplay,
  resolveMonthKpiKey,
  isCompositeKpi,
  applyCompositeOperand,
} from "../constants/kpi";
import { toErrorMessage } from "../helpers/errorText";
import { companyToday, companyMonthStr } from "../helpers/companyTime";
import { isRateLikeKpi, isWindowRateKpi, isBidAccuracyKpi, isBidAccuracyCell } from "../helpers/scoreAggregate";
import { applyShortWindowRates } from "../helpers/windowRates";
import { pastMonthKeys } from "./usePastMonthKpi";

import moment from 'moment';
import { GET_LINKS } from '../api/links';
import startCase from 'lodash/startCase';

import {
  UseSelectedWorkspace,
  UseUserIdList,
  UseUsersDictionary,
  UseYetiStats,
  buildYetiScoresForKpis,
  mergeSupplementalYetiScores,
  yetiScoringKpis,
} from "./company";
import yetiProfile from "../constants/yetiProfile";
import { 
  ADD_USER_KPI, 
  GET_GROUP_KPIS, 
  GET_ALL_USER_KPIS, 
  UPDATE_USER_KPI, 
  DELETE_USER_KPI,
  UPDATE_TOTAL_SCORE,
  UPDATE_GROUP_KPI,
  UPDATE_KPI_GUIDE_TEXT,
  UPDATE_KPI_DAILY_GOAL_TEXT,
  CREATE_CUSTOM_KPI,
  DISABLE_GROUP_KPI,
} from '../api/kpi';
import { refreshKpis, getPreviousKpis as getPreviousKpisAction } from '../redux/kpi/actions';
import socket from '../configs/socket';
import socketConstants from '../constants/socket';

const { SOCKET_EVENT } = socketConstants;

const addKpiObjects = (kpiSum, plusKpi) => {
  Object.keys(plusKpi).forEach((key) => {
    if (kpiSum[key]) {
      kpiSum[key].pts += plusKpi[key]?.pts;
      kpiSum[key].amt += plusKpi[key]?.amt;
    } else {
      kpiSum[key] = { ...plusKpi[key] };
    }
    kpiSum.total += plusKpi[key]?.pts ?? 0;
  });
  return kpiSum;
};

/**
 * Build per-day company KPI totals for a given month from user scores.
 * Used by Company Metrics to power Day/Week chart views.
 * @param {Array} userScores - Same shape as GET_ALL_USER_KPIS scores: [{ ownerId, days: [{ day, kpis }, ...] }]
 * @param {moment.Moment} monthMoment - Start of month (e.g. moment().startOf('month'))
 * @returns {Object} - { "YYYY-MM-DD": { [kpiId]: { amt, pts }, total }, ... }
 */
export function buildCompanyScoresByDay(userScores, monthMoment) {
  const start = moment(monthMoment).startOf("month");
  const end = moment(start).endOf("month");
  const byDay = {};

  (userScores || []).forEach((userScore) => {
    (userScore.days || []).forEach((dayData) => {
      const dateRef = moment(dayData.day);
      if (dateRef.isBefore(start) || dateRef.isAfter(end)) return;
      const dateKey = dateRef.format("YYYY-MM-DD");
      if (!byDay[dateKey]) byDay[dateKey] = { total: 0 };
      addKpiObjects(byDay[dateKey], dayData.kpis || {});
    });
  });

  return byDay;
}

/**
 * Format chart data for Day view: one point per day of the month (1..N), value 0 if no data.
 */
export function formatSparklineDaily(companyScoresByDay, kpiId, monthMoment) {
  const start = moment(monthMoment).startOf("month");
  const daysInMonth = start.daysInMonth();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = moment(start).add(i, "days");
    const dateKey = d.format("YYYY-MM-DD");
    const dayVal = companyScoresByDay?.[dateKey]?.[kpiId];
    let value = 0;
    if (dayVal && typeof dayVal.amt === "number") value = dayVal.amt;
    else if (typeof dayVal === "number") value = dayVal;
    return { timestamp: String(i + 1), value };
  });
}

/**
 * Format chart data for Week view: exactly 4 points (Week 1..4), aggregated by calendar week in month.
 */
export function formatSparklineWeekly(companyScoresByDay, kpiId, monthMoment) {
  const start = moment(monthMoment).startOf("month");
  const daysInMonth = start.daysInMonth();
  const weekRanges = [
    [1, Math.min(7, daysInMonth)],
    [8, Math.min(14, daysInMonth)],
    [15, Math.min(21, daysInMonth)],
    [22, daysInMonth],
  ];

  return weekRanges.map(([fromDay, toDay], idx) => {
    let value = 0;
    for (let d = fromDay; d <= toDay; d++) {
      const dateKey = moment(start).date(d).format("YYYY-MM-DD");
      const dayVal = companyScoresByDay?.[dateKey]?.[kpiId];
      let amt = 0;
      if (dayVal && typeof dayVal.amt === "number") amt = dayVal.amt;
      else if (typeof dayVal === "number") amt = dayVal;
      value += amt;
    }
    return { timestamp: `Week ${idx + 1}`, value };
  });
}

/**
 * Get chart (sparkline) data for a company metric: daily (full month) or weekly (4 points).
 */
export function getCompanyMetricChartData(companyScoresByDay, kpiId, monthMoment, granularity = "day") {
  if (!companyScoresByDay || !kpiId || !monthMoment) {
    return [{ timestamp: "1", value: 0 }];
  }
  const month = moment(monthMoment).startOf("month");
  return granularity === "week"
    ? formatSparklineWeekly(companyScoresByDay, kpiId, month)
    : formatSparklineDaily(companyScoresByDay, kpiId, month);
}

export const useKpiData = () => {
  const dispatch = useDispatch();
  const authenticated = UseAuth();
  const { _id: userId, companyId } = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const workspace = UseSelectedWorkspace();
  const isLoading = useSelector((state) => state.kpiReducer.isLoading);

  const fetchKpiData = useCallback(async (options = {}) => {
    if (!authenticated || !workspace?._id) return;

    try {
      dispatch({ type: types.REFRESH_KPIS });
      
      // Fetch group KPIs. includeRetired also returns long-disabled metrics
      // (tagged `retired`) so Past Boards can keep rendering their history; they
      // are kept out of the live catalog below.
      const targetKpis = await GET_GROUP_KPIS(companyId, null, true);
      
      if (!targetKpis.data?.length) {
        dispatch({ type: types.GET_KPI_SCORE_EMPTY });
        return;
      }

      // Process company KPIs (include appearance and chartGranularity so metric card can show correct chart type/view)
      const allCompanyKpis = targetKpis.data.map(({
        _id, name, pointScale, target, requiredInputs, model,
        groupId, linkedGroupIds, parameters, sourceId, source, integration,
        integrationId, integrationMeta, isAdminAccess, metricType,
        optional, isDraft, isSaved, pointValue, createdAt, disabled, retired,
        retiredFrom,
        connectionStatus, lastSyncedAt, lastSyncedReliable,
        appearance,
        chartGranularity,
        guideText,
        dailyGoalText,
      }) => ({
        _id,
        groupId,
        linkedGroupIds,
        metricType,
        optional,
        isDraft,
        isSaved,
        pointValue: pointValue ?? model.defaultPointValue ?? 0,
        pointScale: pointScale ?? model.defaultPointScale,
        defaultPointScale: model.defaultPointScale,
        target: target ?? model.defaultTarget,
        title: startCase(name ?? model.type),
        kind: model.kind,
        type: model.type,
        kpiModelId: model._id,
        direction: model.direction,
        isManual: !sourceId,
        isShared: !!(parameters?.level || source?.parameters?.level),
        isCustom: model.isTemplate === true,
        isAdminAccess: isAdminAccess ?? false,
        integrationId,
        ...(integrationMeta && { integrationMeta }),
        sourceId,
        source: integration?.code,
        sourceName: integration?.name,
        // The specific integration mapping's own blurb (e.g. "Total revenue
        // from completed and invoiced jobs") — feeds the Scorekeeper
        // integrated-metric info popover. `source` above is already
        // overloaded as the integration CODE string, so this can't reuse
        // that name.
        sourceDescription: source?.description || null,
        // Real connection state from the company's Integration doc (set by
        // getCompanyKpis) — only present when this metric is integration-fed.
        // lastSyncedReliable is false for the couple of integrations whose
        // pull code never advances lastUpdate (ServiceMonster, Facebook Ads),
        // so consumers should hide the timestamp rather than show a stale one.
        connectionStatus,
        lastSyncedAt,
        lastSyncedReliable,
        // "sum" and "memberships" track a per-working-day target that ramps
        // with elapsed weekdays — they must not be treated as whole-month flat.
        wholeMonth:
          source?.parameters?.calc &&
          source?.parameters?.calc !== "sum" &&
          source?.parameters?.calc !== "memberships",
        calc: source?.parameters?.calc,
        gap: source?.parameters?.gap,
        minimum: parameters?.minimum ?? 0,
        parameters: parameters ?? null,
        requiredInputs: requiredInputs?.length > 0 ? requiredInputs : model.defaultInputs ?? null,
        date: createdAt,
        disabled,
        retired,
        ...(retiredFrom ? { retiredFrom } : {}),
        ...(appearance !== undefined && { appearance }),
        ...(chartGranularity !== undefined && { chartGranularity }),
        ...(typeof guideText === "string" && { guideText }),
        ...(typeof dailyGoalText === "string" && { dailyGoalText }),
      })).sort((a, b) => b.pointScale * b.target - a.pointScale * a.target);

      // Separate draft and active KPIs. companyKpis is the LIVE catalog
      // (active + recently-disabled, non-retired); companyAllKpis additionally
      // carries retired metrics so historic boards can still render them.
      const draftKpis = allCompanyKpis.filter(kpi => kpi.isDraft);
      const companyKpis = {};
      const companyAllKpis = {};
      const addToKpiBucket = (bucket, workspaceId, groupKpi) => {
        if (!workspaceId) return;
        if (bucket[workspaceId]) bucket[workspaceId].push(groupKpi);
        else bucket[workspaceId] = [groupKpi];
      };
      allCompanyKpis.filter(kpi => !kpi.isDraft).forEach((groupKpi) => {
        // A metric assigned to more than one workspace via "Assign to
        // workspace(s)" carries the SAME document/kpiId into every board it's
        // linked to (see GroupKpi.linkedGroupIds) — not a copy, so scores
        // written under this one kpiId (e.g. by the HCP hours sync) render
        // correctly on every board it appears on.
        // Dedupe defensively: if linkedGroupIds ever contains the doc's own
        // groupId (shouldn't happen server-side, but this is the render path —
        // don't let stray data double-render one card on the same board).
        const boardIds = [...new Set([groupKpi.groupId, ...(groupKpi.linkedGroupIds || [])].map(String))];
        boardIds.forEach((id) => addToKpiBucket(companyAllKpis, id, groupKpi));
        if (groupKpi.retired) return;
        boardIds.forEach((id) => addToKpiBucket(companyKpis, id, groupKpi));
      });

      // Process workspace KPIs
      let groupKpis = [];
      let groupAllKpis = [];
      const applyLinksTo = (arr, links) =>
        (arr ?? []).map((groupKpi) => {
          const kpiLink = links.data.data.find((link) => link.kpiId === groupKpi._id);
          return kpiLink ? { ...groupKpi, kpiLinks: kpiLink } : groupKpi;
        });
      if (isAdmin) {
        const links = await GET_LINKS(companyId);
        groupKpis = applyLinksTo(
          (companyKpis[workspace._id] ?? []).filter(kpi => !kpi.disabled),
          links,
        );
        groupAllKpis = applyLinksTo(companyAllKpis[workspace._id], links);
      } else {
        groupKpis = (companyKpis[workspace._id] ?? []).filter(kpi => !kpi.disabled);
        // Players need groupAllKpis too — Past Boards columns + the scoped points
        // recompute both read from it. Without this it stays empty, so the player
        // board falls back to raw (unscoped) totals and a different column order.
        // Includes retired metrics so their history keeps rendering.
        groupAllKpis = companyAllKpis[workspace._id] ?? [];
      }

      // Fetch and process user scores
      const scoreKpis = await GET_ALL_USER_KPIS(companyId, workspace._id, null, {
        bustCache: Boolean(options.bustCache),
      });

      const userScores = [];
      const sharedScores = [];

      scoreKpis.data.scores.forEach((score) => {
        if (score.scope === "User") {
          userScores.push(score);
        } else {
          sharedScores.push(...score.days);
        }
      });

      if (sharedScores.length) {
        scoreKpis.data.users.forEach((user) => {
          const idx = userScores.findIndex((u) => u.ownerId === user);
          if (idx >= 0) {
            userScores[idx].days.push(...sharedScores);
          } else {
            userScores.push({
              ownerId: user,
              days: [...sharedScores],
            });
          }
        });
      }

      // Calculate time-based summaries (company/Pacific time, not viewer's)
      const day = companyToday();
      const yesterday = companyToday().subtract(1, "days");
      const week = companyToday().startOf("week");
      const lastWeek = companyToday().subtract(1, "week").startOf("week");
      const month = companyToday().startOf("month");

      let myKpis = null;
      const usersKpis = Array.isArray(userScores) ? userScores.map((userScore) => {
        const summary = {
          userId: userScore.ownerId,
          yesterday: { total: 0 },
          today: { total: 0 },
          week: { total: 0 },
          lastWeek: { total: 0 },
          month: { total: 0 },
        };

        /* Map KPI id → calc so month totals don't SUM rate snapshots. */
        const kpiMetaById = {};
        (groupKpis || []).forEach((k) => {
          if (k?._id) kpiMetaById[String(k._id)] = k;
        });
        (Object.values(companyKpis || {}) || []).flat().forEach((k) => {
          if (k?._id) kpiMetaById[String(k._id)] = k;
        });

        userScore.days.forEach((dayData) => {
          const dateRef = moment(dayData.day);
          if (dateRef >= day) {
            addKpiObjects(summary.today, dayData.kpis);
          } else if (dateRef >= yesterday) {
            addKpiObjects(summary.yesterday, dayData.kpis);
          }
          if (dateRef >= week) {
            addKpiObjects(summary.week, dayData.kpis);
          } else if (dateRef >= lastWeek) {
            addKpiObjects(summary.lastWeek, dayData.kpis);
          }
          if (dateRef >= month) {
            addKpiObjects(summary.month, dayData.kpis);
          }
        });

        /* Other month-pinned rate KPIs miss Today/Week after the 1st — copy
           from month when the short bucket has no real value. Closed Rate,
           Bid Accuracy, and AVG Ticket recompute per window via
           /kpi/window-rates instead (isWindowRateKpi). */
        const copyPinnedRates = (bucket) => {
          Object.entries(kpiMetaById).forEach(([id, meta]) => {
            if (!isRateLikeKpi(meta)) return;
            if (isWindowRateKpi(meta)) return;
            if ((Number(bucket[id]?.amt) || 0) > 0) return;
            const src = summary.month[id];
            if ((Number(src?.amt) || 0) <= 0) return;
            const { __day, ...rest } = src;
            bucket[id] = { ...rest };
          });
        };
        copyPinnedRates(summary.today);
        copyPinnedRates(summary.yesterday);
        copyPinnedRates(summary.week);
        copyPinnedRates(summary.lastWeek);

        if (summary.userId === userId) {
          myKpis = summary;
        }

        return summary;
      }) : [];

      // Closed Rate + Bid Accuracy for Today / Week / … (not month-pinned copy).
      if (workspace?._id && usersKpis.length) {
        await applyShortWindowRates({
          groupId: workspace._id,
          usersKpis,
          myKpis,
          myUserId: userId,
        });
      }

      // Calculate admin view averages, per period. The navbar filter follows
      // these, so we build the team aggregate for every bucket the same way —
      // not just month. `buildAdminBucket("month")` is identical to the old
      // month-only code, so Company View / TV (which read adminKpis.month) are
      // unchanged; the per-active-player averaging still happens in UseMyKpis.
      const adminBidAccuracyIds = new Set();
      [...(groupKpis || []), ...(Object.values(companyKpis || {}) || []).flat()].forEach((k) => {
        if (k?._id && isBidAccuracyKpi(k)) adminBidAccuracyIds.add(String(k._id));
      });
      const buildAdminBucket = (bucketName) => {
        const totals = { total: 0 };
        const teamScores = usersKpis
          .map(scores => {
            const { total, ...rest } = scores[bucketName] || { total: 0 };
            return total ? rest : null;
          })
          .filter(Boolean);

        teamScores.forEach(scores => {
          addKpiObjects(totals, scores);
        });

        Object.keys(totals).forEach(key => {
          if (key !== "total") {
            const scorersCount = teamScores.reduce(
              (count, score) => count + (score[key] ? 1 : 0),
              0
            );
            // 4 decimals only for Bid Accuracy (0.896 = 89.6%); 2 decimals would round it to a whole percent. Every other metric keeps 2.
            totals[key].amt = adminBidAccuracyIds.has(String(key))
              ? Math.round(totals[key].amt * 10000) / 10000
              : Math.round(totals[key].amt * 100) / 100;
            totals[key].pts = Math.round(totals[key].pts * 100) / 100;
            totals[key].scorersCount = scorersCount;
          } else {
            totals.total = teamScores.length
              ? Math.round((totals.total / teamScores.length) * 100) / 100
              : 0;
          }
        });
        return totals;
      };

      dispatch({
        type: types.GET_KPI_SCORE_SUCCESS,
        companyKpis,
        groupKpis,
        groupAllKpis,
        draftKpis,
        usersKpis: usersKpis.sort((a, b) => b.month.total - a.month.total),
        myKpis,
        adminKpis: {
          today: buildAdminBucket("today"),
          yesterday: buildAdminBucket("yesterday"),
          week: buildAdminBucket("week"),
          lastWeek: buildAdminBucket("lastWeek"),
          month: buildAdminBucket("month"),
        },
      });
    } catch (err) {
      dispatch({ type: types.GET_KPI_SCORE_FAILED, error: err.message });
    }
  }, [authenticated, companyId, dispatch, isAdmin, userId, workspace]);

  useEffect(() => {
    fetchKpiData();
  }, [fetchKpiData]);

  const usersKpisLive = useSelector((state) => state.kpiReducer.usersKpis);
  const myKpisLive = useSelector((state) => state.kpiReducer.myKpis);
  const usersKpisRef = useRef(usersKpisLive);
  const myKpisRef = useRef(myKpisLive);
  usersKpisRef.current = usersKpisLive;
  myKpisRef.current = myKpisLive;

  // Automation give/deduct points rebuild DailyScoreView server-side, then
  // push KPIS_UPDATED. Optimistically patch Redux so Scorekeeper boxes move
  // immediately, then refetch with a cache-buster.
  useEffect(() => {
    if (!authenticated || !companyId) return undefined;

    const bumpPeriod = (period, kpiId, pts, amt) => {
      if (!period) return period;
      const next = { ...period };
      const row = next[kpiId] ? { ...next[kpiId] } : { pts: 0, amt: 0 };
      row.pts = (Number(row.pts) || 0) + pts;
      row.amt = (Number(row.amt) || 0) + amt;
      next[kpiId] = row;
      next.total = (Number(next.total) || 0) + pts;
      return next;
    };

    const handler = (data) => {
      if (data?.companyId && String(data.companyId) !== String(companyId)) return;

      const pts = Number(data?.points) || 0;
      const amt = Number(data?.amount) || 0;
      const kpiId = data?.kpiId ? String(data.kpiId) : "";
      const uid = data?.userId ? String(data.userId) : "";

      if (kpiId && uid && (pts || amt)) {
        const list = Array.isArray(usersKpisRef.current)
          ? usersKpisRef.current.map((row) => ({ ...row }))
          : [];
        let userRow = list.find((row) => String(row.userId) === uid);
        if (!userRow) {
          userRow = {
            userId: uid,
            yesterday: { total: 0 },
            today: { total: 0 },
            week: { total: 0 },
            month: { total: 0 },
          };
          list.push(userRow);
        } else {
          userRow = { ...userRow };
          const idx = list.findIndex((row) => String(row.userId) === uid);
          list[idx] = userRow;
        }
        userRow.today = bumpPeriod(userRow.today || { total: 0 }, kpiId, pts, amt);
        userRow.week = bumpPeriod(userRow.week || { total: 0 }, kpiId, pts, amt);
        userRow.month = bumpPeriod(userRow.month || { total: 0 }, kpiId, pts, amt);
        dispatch({ type: types.UPDATE_MONTH_KPIS, usersKpis: list });

        if (String(userId) === uid && myKpisRef.current) {
          const mine = { ...myKpisRef.current };
          mine.today = bumpPeriod(mine.today || { total: 0 }, kpiId, pts, amt);
          mine.week = bumpPeriod(mine.week || { total: 0 }, kpiId, pts, amt);
          mine.month = bumpPeriod(mine.month || { total: 0 }, kpiId, pts, amt);
          dispatch({ type: types.UPDATE_MY_KPIS, myKpis: mine });
        }
      }

      fetchKpiData({ bustCache: true });
    };
    socket.on(SOCKET_EVENT.KPIS_UPDATED, handler);
    return () => socket.off(SOCKET_EVENT.KPIS_UPDATED, handler);
  }, [authenticated, companyId, dispatch, fetchKpiData, userId]);

  return { isLoading, refetch: fetchKpiData };
}; 

export const UseKpiRefresh = () => {
  const { refetch } = useKpiData();
  return refetch;
};

export const UseKpiLoading = () => {
  const { isLoading } = useKpiData();
  return isLoading;
};

export const UseGroupKpis = () => {
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  return groupKpis;
};

export const UseGroupAllKpis = () => {
  const groupAllKpis = useSelector((state) => state.kpiReducer.groupAllKpis);
  return groupAllKpis;
};

export const UseCompanyKpis = () => {
  const companyKpis = useSelector((state) => state.kpiReducer.companyKpis);
  return companyKpis;
};

/**
 *
 * @param {string} period accepts yesterday, today, week, lastWeek, month
 * @param {*} month date reference
 * @returns array of player score objects in descending score order
 */
export const UseTopPlayers = (period = "month", month = null) => {
  const [stats, setStats] = useState(null);
  const monthRef = month?.format("YYYY-MM");
  const usersKpis = useSelector((state) =>
    monthRef
      ? state.kpiReducer.historicKpis?.[monthRef]
      : state.kpiReducer.usersKpis,
  );
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const groupAllKpis = useSelector((state) => state.kpiReducer.groupAllKpis);
  const holidays = useSelector((state) => state.companyReducer.holidays);
  const userId = useSelector((state) => state.authReducer.userDetails?._id);
  const usersDict = UseUsersDictionary();
  const yeti = UseYetiStats(!monthRef);

  useEffect(() => {
    if (monthRef && !Array.isArray(usersKpis)) {
      setStats(null);
      return;
    }
    if (usersDict && Array.isArray(usersKpis)) {
      const topPlayers = usersKpis.map((item) => ({
        userId: item.userId,
        scores: item[period],
        today: item.today?.total,
        yesterday: item.yesterday?.total,
        yesterdayRank: item.yesterdayRank,
        ...usersDict[item.userId],
        ...(item.userId === userId && { ownId: true }),
      }));

      if (yeti) {
        let yetiScores = yeti;
        // Historic months can show KPIs from groupAllKpis (e.g. Past Boards
        // supplemental metrics) that aren't in workspace groupKpis. Yeti must
        // carry benchmark amounts for those columns too.
        if (monthRef) {
          const kpiIdsWithScores = new Set();
          usersKpis.forEach((user) => {
            const bucket = user[period] || user.month;
            if (!bucket) return;
            Object.keys(bucket).forEach((kpiId) => {
              if (kpiId !== "total" && bucket[kpiId]) {
                kpiIdsWithScores.add(String(kpiId));
              }
            });
          });

          const yetiPeriod = yeti[period] || {};
          const missingIds = [...kpiIdsWithScores].filter((id) => !yetiPeriod[id]);
          if (missingIds.length) {
            const kpiById = {};
            [...(groupKpis || []), ...(groupAllKpis || [])].forEach((k) => {
              if (k?._id) kpiById[String(k._id)] = k;
            });
            // Enabled metrics only — a disabled one (e.g. a test metric a few
            // players still have September rows on) must not earn the Yeti a
            // full month of benchmark points. See yetiScoringKpis.
            const missingKpis = yetiScoringKpis(
              missingIds.map((id) => kpiById[id]),
            );
            if (missingKpis.length) {
              const supplemental = buildYetiScoresForKpis(
                missingKpis,
                monthRef,
                holidays,
              );
              yetiScores = mergeSupplementalYetiScores(yeti, supplemental);
            }
          }
        }

        topPlayers.push({
          ...yetiProfile,
          scores: yetiScores[period],
          today: yetiScores.today.total,
          yesterday: yetiScores.today.total,
          isYeti: true,
        });
      }

      if (!monthRef) {
        // Previous month's scores; skip yesterday comparison
        // Order based on yesterday's scores and record ranking
        if (period === "today") {
          // Compare points yesterday versus today
          topPlayers.sort((a, b) => b.yesterday - a.yesterday);
        } else {
          // Compare points yesterday versus today, but relative to the month or week's standing
          topPlayers.sort(
            (a, b) => b.scores?.total - b.today - (a.scores?.total - a.today),
          );
        }
        topPlayers.forEach((user, index) => {
          user.yesterdayRank = index + 1;
        });
      }
      topPlayers.sort((a, b) => b.scores?.total - a.scores?.total);
      setStats(topPlayers);
    }
  }, [period, yeti, usersKpis, usersDict, monthRef, userId, groupKpis, groupAllKpis, holidays]);

  return stats;
};

/**
 * Returns user scores, returns workspace average instead if admin
 * @param {bool} forceAdmin if true, returns workspace average scores whether current user is admin or not
 */
export const UseMyKpis = (forceAdmin = false) => {
  const myKpis = useSelector((state) => state.kpiReducer.myKpis);
  const adminKpis = useSelector((state) => state.kpiReducer.adminKpis);
  const usersKpis = useSelector((state) => state.kpiReducer.usersKpis);
  const topKpis = useSelector((state) => state.kpiReducer.usersKpis?.[0]);
  const [kpis, setKpis] = useState(myKpis);
  const isDemo = UseIsDemo();
  const isAdmin = UseIsAdmin();
  const topPlayersThisMonth = UseTopPlayers("month");

  useEffect(() => {
    const filteredTopPlayers = topPlayersThisMonth?.filter(player => 
      player.isActive !== false && player.lastName !== "Yeti"
    ) || [];
    const inactivePlayers = topPlayersThisMonth?.filter(player => 
      player.isActive === false
    ) || [];


    if (isDemo) {
      setKpis(topKpis);
    } else if (isAdmin || forceAdmin) {
      if (adminKpis?.month) {
        // Average every period bucket the same way — per active player — so the
        // navbar filter changes the admin numbers, not just the leaderboard.
        // Each inactive player's contribution is looked up in usersKpis FOR
        // THAT bucket (for month this equals the old topPlayers.scores, so the
        // month numbers are unchanged).
        const avgBucket = (bucketName) => {
          const src = adminKpis[bucketName];
          if (!src) return src;
          return Object.keys(src).reduce((acc, key) => {
            if (src[key]?.amt !== undefined) {
              const inactiveKpiAmtSum = inactivePlayers.reduce((sum, player) => {
                const u = usersKpis?.find((x) => x.userId === player.userId);
                return sum + (u?.[bucketName]?.[key]?.amt || 0);
              }, 0);

              const inactiveKpiPtsSum = inactivePlayers.reduce((sum, player) => {
                const u = usersKpis?.find((x) => x.userId === player.userId);
                return sum + (u?.[bucketName]?.[key]?.pts || 0);
              }, 0);

              // Average over the active players who actually have entries for
              // THIS metric IN THIS bucket — dividing by the whole roster let
              // players with no entries dilute the average (e.g. Sales Closed
              // Rate showed 32% instead of 48% because a rep with no estimates
              // counted as a 0).
              const activeScorers = filteredTopPlayers.filter((player) => {
                if (bucketName === "month") return player.scores?.[key];
                const u = usersKpis?.find((x) => x.userId === player.userId);
                return u?.[bucketName]?.[key];
              }).length;

              acc[key] = {
                ...src[key],
                amt: activeScorers > 0 ? (src[key].amt - inactiveKpiAmtSum) / activeScorers : 0,
                pts: activeScorers > 0 ? (src[key].pts - inactiveKpiPtsSum) / activeScorers : 0
              };
            } else {
              acc[key] = src[key];
            }
            return acc;
          }, {});
        };
        const averagedKpis = {
          ...adminKpis,
          today: avgBucket("today"),
          yesterday: avgBucket("yesterday"),
          week: avgBucket("week"),
          lastWeek: avgBucket("lastWeek"),
          month: avgBucket("month"),
        };
        setKpis(averagedKpis);
      }
    } else {
      setKpis(myKpis);
    }
    storeMyKpis(myKpis);
  }, [myKpis, adminKpis, usersKpis, topKpis, isDemo, isAdmin, forceAdmin, topPlayersThisMonth]);

  return kpis;
};

/**
 * Mid-life retirement cutoff: GroupKpi.retiredFrom = "YYYY-MM".
 * From that month onward the metric is hidden on Past Boards / TV Last Month
 * even if it still has scores (e.g. "Revenue" retiredFrom "2026-07").
 */
export const isKpiHiddenForMonth = (kpi, monthKey) =>
  Boolean(kpi?.retiredFrom) && Boolean(monthKey) && monthKey >= kpi.retiredFrom;

/**
 * The retired In-House "Revenue" and the legacy Technicians "revenue" must not
 * appear on Past Boards, TV, or past-month Scorekeeper. Scoped by GroupKpi id —
 * an exact-title /^revenue$/ match here once blanket-hid every workspace's
 * Revenue metric (e.g. Sales' active Revenue vanished from all past months).
 *
 * June-only (2026-06): drop Scorekeeper test columns + active justine
 * "Revenue Credit" (which collides with its retired predecessor that month)
 * and two mid-month experiment Revenue Credit copies — same code-only
 * approach as July Revenue, without touching prod GroupKpi docs.
 * Also drops "Rev Credit Hour" (column + Scorekeeper month total / computation).
 * July+ past months still show active justine Revenue Credit.
 *
 * Also drops mid-life retiredFrom metrics for the given month.
 */
const ALWAYS_EXCLUDED_KPI_IDS = new Set([
  '694306e6fcf5044f4812c608', // In-House Sales "Revenue" (retiredFrom 2026-07)
  '62f0d63c17ceab3cbca39593', // Technicians legacy "revenue" (disabled 2023 clone)
]);
const EXACT_EXCLUDED_JUNE_ONLY =
  /^(revenue credit per hour test|rev cred per hr points|rev credit hour)$/i;
const JUNE_EXCLUDE_MONTH = '2026-06';
/** Active justine Revenue Credit — predecessor is 689de73d05af90d53604a25e. */
const ACTIVE_REVENUE_CREDIT_ID = '6a2ffe68a1042c14f1138e3e';
/** June 2026 experiment duplicate Revenue Credit GroupKpi ids. */
const JUNE_EXCLUDED_REVENUE_CREDIT_IDS = new Set([
  '6a26da09c1c9f8c2f16427c3',
  '6a28028ec1c9f8c2f15e3e9a',
]);

// Rollup metrics: company/workspace-wide numbers with no player behind them
// (see EmployeeKpi.ownerModel: 'Group') — one shared value for the whole
// board, not a per-player stat. Scoped to the specific KpiModel types
// (total_revenue, team_hours, scheduled_revenue) rather than a bare
// metricType check, so unrelated integration metrics that start life as
// "display" while an admin finishes configuring them are never caught by
// this.
//
// Deliberately independent of metricType: an admin can gamify a rollup GroupKpi
// (give it a target/pointsRule, metricType 'progressive'/'anchor') to earn points
// off it, but that doesn't put a player behind the number — Total Revenue stayed
// a single company-wide figure and still leaked onto a workspace's Leaderboard
// this way. Excluding by model type alone keeps it off every board regardless of
// how it's configured, while the points it earns still count toward totals
// (see isExcludedFromBoards' callers).
const ROLLUP_KPI_TYPES = new Set(['total_revenue', 'team_hours', 'scheduled_revenue']);
const isRollupKpi = (kpi) =>
  ROLLUP_KPI_TYPES.has(kpi?.type || kpi?.model?.type);

export const isExcludedFromBoards = (kpi, monthKey) => {
  // Rollup metrics (Team Hours, Total Revenue, ...) were never meant to
  // appear as an editable per-player column on Scorekeeper, Past Boards, or
  // TV — there is no player behind the number to score.
  if (isRollupKpi(kpi)) return true;

  // Match specific GroupKpi ids only — NOT kpi.type and NOT a bare title
  // regex. `type` is the shared KpiModel type (HCP metrics like Estimate Won
  // Value and Commission are built on the "revenue" model), and a title match
  // hid every workspace's "Revenue" metric instead of just the retired ones.
  const labels = [kpi?.title, kpi?.name]
    .filter(Boolean)
    .map((s) => String(s).trim());
  const id = String(kpi?._id || '');
  if (ALWAYS_EXCLUDED_KPI_IDS.has(id)) return true;
  if (
    monthKey === JUNE_EXCLUDE_MONTH &&
    (labels.some((label) => EXACT_EXCLUDED_JUNE_ONLY.test(label)) ||
      id === ACTIVE_REVENUE_CREDIT_ID ||
      JUNE_EXCLUDED_REVENUE_CREDIT_IDS.has(id))
  ) {
    return true;
  }
  return isKpiHiddenForMonth(kpi, monthKey);
};

/** Workspace KPIs shown as public columns/cards — excludes scorekeeper-only and rollup metrics (e.g. Total Revenue); totals still include those points. */
export const UseLeaderboardGroupKpis = () => {
  const groupKpis = UseGroupKpis();
  return useMemo(
    () => filterKpisForPublicDisplay((groupKpis || []).filter((k) => !isExcludedFromBoards(k))),
    [groupKpis],
  );
};

// Helper function to filter KPIs by creation date
export const filterKpisByCreationDate = (kpis, selectedMonth) => {
  if (!Array.isArray(kpis)) return [];
  if (!selectedMonth) {
    return kpis;
  }

  const selectedMoment = moment(selectedMonth);

  return kpis.filter(kpi => {
    if (!kpi.date) {
      return true; // Include KPIs without a creation date
    }

    // Read the creation month in UTC (how the server stores it). Parsing in
    // the viewer's local zone pushed e.g. a Jul 31 21:37Z creation into
    // "August" for UTC+ browsers, hiding the metric from its own first month.
    const kpiCreatedMonth = moment.utc(kpi.date).format('YYYY-MM');
    // Only show KPI if it was created in or before the selected month
    return kpiCreatedMonth <= selectedMoment.format('YYYY-MM');
  });
};

// Helper function to filter KPIs by whether they have user scores in the given month
export const filterKpisByUserScores = (kpis, usersKpis) => {
  if (!Array.isArray(kpis)) return [];
  if (!Array.isArray(usersKpis) || usersKpis.length === 0) {
    return [];
  }

  const kpiIdsWithScores = new Set();
  usersKpis.forEach((user) => {
    if (user.month) {
      Object.keys(user.month).forEach((kpiId) => {
        if (kpiId !== "total" && user.month[kpiId]) {
          kpiIdsWithScores.add(String(kpiId));
        }
      });
    }
  });

  return kpis.filter((kpi) =>
    [...kpiIdsWithScores].some((id) => String(id) === String(kpi._id)),
  );
};

/** Empty usersKpis row shape (matches redux/kpi saga). */
function createEmptyUserKpiSummary(userId) {
  return {
    userId,
    yesterday: { total: 0 },
    today: { total: 0 },
    week: { total: 0 },
    month: { total: 0 },
  };
}

function findKpiInMap(kpiById, key) {
  if (kpiById[key]) return kpiById[key];
  return Object.values(kpiById).find((k) => String(k._id) === String(key));
}

// table of user KPI scores
export const UseKpiScoresTable = (month = null, workspaceId = null) => {
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const groupAllKpis = useSelector((state) => state.kpiReducer.groupAllKpis);
  const monthRef = month?.format("YYYY-MM");
  const monthToday = companyMonthStr();
  
  const usersKpis = useSelector((state) =>
    monthRef
      ? state.kpiReducer.historicKpis?.[monthRef]
      : state.kpiReducer.usersKpis,
  );

  const usersDict = UseUsersDictionary();
  const usersList = UseUserIdList();
  const isCurrentMonth = !monthRef || monthRef === monthToday;

  const usedGroupKpisMemo = useMemo(() => {
    if (month) {
      if (monthRef === monthToday) {
        return filterKpisByCreationDate(groupKpis, month) || [];
      }
      const creationFiltered = filterKpisByCreationDate(groupAllKpis, month) || [];
      return filterKpisByUserScores(creationFiltered, usersKpis)
        .filter((kpi) => !isExcludedFromBoards(kpi, monthRef)) || [];
    }
    return groupKpis || [];
  }, [month, monthRef, monthToday, groupKpis, groupAllKpis, usersKpis]);

  // Derive synchronously so switching July → live never returns the previous
  // month's rows for a frame (useState+useEffect lagged and fed July into
  // TotalScoreCell initialAmount).
  return useMemo(() => {
    const newKpiTable = [];
    const allUsers = [...(usersList || [])];
    const safeGroupKpis = groupKpis || [];

    if (usersKpis?.length) {
      for (let u = 0; u < usersKpis.length; u++) {
        const user = usersKpis[u];
        const { groupId, isArchived } = usersDict[user.userId] ?? {};

        if (isArchived || groupId !== workspaceId) continue;

        const ptsPerAmtOf = (kpi) => {
          const s = Number(kpi?.pointScale) || 0;
          const v = Number(kpi?.pointValue) || 0;
          return s > 0 ? v / s : v;
        };

        // Matches the live leaderboard's recomputeScopedPlayerScores rule:
        //   - anchor → trust server pts
        //   - non-anchor → amt × (pointValue/pointScale) ONLY when BOTH are
        //     present and non-zero; otherwise fall back to the server-stored
        //     pts (the user's actual score), same as /scorekeeper timeline.
        const ptsForRow = (kpi, row) => {
          if (kpi?.metricType === "anchor") return Number(row?.pts) || 0;
          const hasFullConfig =
            Number(kpi?.pointValue) > 0 && Number(kpi?.pointScale) > 0;
          return hasFullConfig
            ? (Number(row?.amt) || 0) * ptsPerAmtOf(kpi)
            : Number(row?.pts) || 0;
        };

        const kpiScores = {};
        let computedTotal = 0;

        if (isCurrentMonth) {
          for (let k = 0; k < usedGroupKpisMemo.length; k++) {
            const kpi = usedGroupKpisMemo[k];
            const kpiId = kpi._id;
            const exists = safeGroupKpis.some((g) => g._id === kpiId);
            const monthKey = resolveMonthKpiKey(user.month, kpiId);
            if (exists && user.month?.[monthKey]) {
              kpiScores[kpiId] = user.month[monthKey].amt;
              computedTotal += ptsForRow(kpi, user.month[monthKey]);
            } else if (exists) {
              kpiScores[kpiId] = 0;
            }
          }
        } else {
          for (let k = 0; k < usedGroupKpisMemo.length; k++) {
            const kpi = usedGroupKpisMemo[k];
            const kpiId = kpi._id;
            const monthKey = resolveMonthKpiKey(user.month, kpiId);
            if (user.month?.[monthKey]) {
              kpiScores[kpiId] = user.month[monthKey].amt;
              computedTotal += ptsForRow(kpi, user.month[monthKey]);
            }
          }
        }

        const userIdx = allUsers.indexOf(user.userId);
        if (userIdx !== -1) allUsers.splice(userIdx, 1);

        newKpiTable.push({
          userId: user.userId,
          info: usersDict[user.userId],
          kpis: kpiScores,
          month: user.month,
          total: computedTotal,
        });
      }
    }

    allUsers.forEach((user) => {
      const { groupId, isArchived } = usersDict[user] ?? {};
      if (isArchived || groupId !== workspaceId) return;

      const emptyScores = {};
      if (isCurrentMonth) {
        usedGroupKpisMemo.forEach((kpi) => {
          if (safeGroupKpis.some((g) => g._id === kpi._id)) {
            emptyScores[kpi._id] = 0;
          }
        });
      }

      newKpiTable.push({
        userId: user,
        info: usersDict[user],
        kpis: isCurrentMonth ? emptyScores : null,
        month: { total: 0 },
        total: 0,
      });
    });

    return newKpiTable;
  }, [
    workspaceId,
    usersKpis,
    usedGroupKpisMemo,
    usersList,
    usersDict,
    groupKpis,
    isCurrentMonth,
  ]);
};

export const UseDraftKpis = () => {
  const draftKpis = useSelector((state) => state.kpiReducer.draftKpis);
  return draftKpis;
};

/**
 * Hook for submitting new KPI records
 */
export const useKpiSubmit = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { _id: adminId, isAdmin } = UseUserDetails();
  const adminKpis = useSelector((state) => state.kpiReducer.adminKpis);
  const myKpis = useSelector((state) => state.kpiReducer.myKpis);
  const monthRef = useSelector(state => state.kpiReducer.usersKpis);

  // let myKpis;
  // if (isAdmin) {
  //   myKpis = adminKpis;
  // } else {
  //   myKpis = userKpis;
  // }

  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const isOnline = useOnlineStatus();

  const submitKpi = async (kpiId, userId, month, data, companyId) => {
    // Fresh id per SAVE, not per render: the entry_dedup unique index rejects
    // a reused (kpiId, ownerId, entryId), so back-to-back saves that shared a
    // render-scoped uuid silently failed with a duplicate-key 500.
    const entryId = generateUuid();
    setIsSubmitting(false);
    setError(null);
    const monthFormatted = month ? moment(month).format("YYYY-MM") : null;
    const kpiData = groupKpis?.find(kpi => kpi._id === kpiId);
    // Optional-chained: adminKpis is {} until the admin summary loads, and an
    // unguarded read here threw BEFORE the request was sent — the entry looked
    // like it "didn't save" while nothing ever reached the server.
    const totalUsersInActiveWorkspace = adminKpis?.month?.[kpiId]?.scorersCount || 0;

    // Convert FormData to plain object for offline storage
    const plainData = {};
    if (data instanceof FormData) {
      Array.from(data.entries()).forEach(([key, value]) => {
        plainData[key] = value;
      });
    } else {
      Object.assign(plainData, data);
    }

    // Offline: queue it. Online photo entries (Yard Sign) go through the awaited
    // online flow below so a failed upload is reported instead of silently dropped.
    if (!isOnline) {
      try {

        // Dispatch the action to handle offline storage
        // Save to offline storage first
        saveOfflineKpi({
          kpiId,
          userId,
          month: monthFormatted,
          data: plainData,
          companyId,
          entryId
        });

        // Update myKpis in the local state to reflect the new score
        if (myKpis) {
          const today = moment().startOf("day");
          const isToday = !month || moment(month).isSame(today, 'day');
          
          // Create a copy of the current KPI data
          const updatedMyKpis = JSON.parse(JSON.stringify(myKpis));
          let pts = 0;
          let amt = 0;
          // if (isAdmin && adminId === userId) {
          //   // Calculate points based on the KPI model
          //   pts = (kpiData?.pointScale * plainData?.amount) / totalUsersInActiveWorkspace || 0;
          //   amt = (parseFloat(plainData?.amount)) / totalUsersInActiveWorkspace || 0;
          // }else{
            // Calculate points based on the KPI model
            pts = kpiData?.pointScale * plainData?.amount || 0;
            amt = parseFloat(plainData?.amount) || 0;
          // }  

          // Update the monthly scores
          const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
          const userData = updatedMonthRef.find(user => user.userId === userId);
          if (userData) {
            if (!userData.month[kpiId]) {
              userData.month[kpiId] = { pts: parseFloat(pts), amt: parseFloat(amt) };
              userData.month.total = parseFloat(userData.month.total || 0) + pts;
            }else{
              userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) + pts;
              userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) + amt;
              userData.month.total = parseFloat(userData.month.total || 0) + pts;
            }
          }

          dispatch({
            type: types.UPDATE_MONTH_KPIS,
            usersKpis: updatedMonthRef
          });

          // if (isAdmin) {
          //   const updatedAdminKpis = { ...myKpis };
            
          //   if (!updatedAdminKpis.month[kpiId]) {
          //     updatedAdminKpis.month[kpiId] = { pts: 0, amt: 0 };
          //   }
          //   if (typeof updatedAdminKpis.month[kpiId].pts !== "number") {
          //     updatedAdminKpis.month[kpiId].pts = pts;
          //   } else {
          //     updatedAdminKpis.month[kpiId].pts += pts;
          //   }
          //   if (typeof updatedAdminKpis.month[kpiId].amt !== "number") {
          //     updatedAdminKpis.month[kpiId].amt = amt;
          //   } else {
          //     updatedAdminKpis.month[kpiId].amt += amt;
          //   }
          //   if (typeof updatedAdminKpis.month.total !== "number") {
          //     updatedAdminKpis.month.total = pts;
          //   } else {
          //     updatedAdminKpis.month.total += pts;
          //   }

          //   dispatch({
          //     type: types.UPDATE_ADMIN_KPIS,
          //     adminKpis: updatedAdminKpis
          //   });
          // } else {// Update the appropriate time period
            if (isToday) {
              // Update today's scores
              if (!updatedMyKpis.today[kpiId]) {
                updatedMyKpis.today[kpiId] = { pts: 0, amt: 0 };
              }
              // Set the values directly instead of adding to prevent concatenation
              updatedMyKpis.today[kpiId].pts = parseFloat(updatedMyKpis.today[kpiId].pts) + pts;
              updatedMyKpis.today[kpiId].amt = parseFloat(updatedMyKpis.today[kpiId].amt) + amt;
              updatedMyKpis.today.total = parseFloat(updatedMyKpis.today.total) + pts;
              
              // Also update week and month
              if (!updatedMyKpis.week[kpiId]) {
                updatedMyKpis.week[kpiId] = { pts: 0, amt: 0 };
              }
              updatedMyKpis.week[kpiId].pts = parseFloat(updatedMyKpis.week[kpiId].pts) + pts;
              updatedMyKpis.week[kpiId].amt = parseFloat(updatedMyKpis.week[kpiId].amt) + amt;
              updatedMyKpis.week.total = parseFloat(updatedMyKpis.week.total) + pts;
              
              if (!updatedMyKpis.month[kpiId]) {
                updatedMyKpis.month[kpiId] = { pts: 0, amt: 0 };
              }
              updatedMyKpis.month[kpiId].pts = parseFloat(updatedMyKpis.month[kpiId].pts) + pts;
              updatedMyKpis.month[kpiId].amt = parseFloat(updatedMyKpis.month[kpiId].amt) + amt;
              updatedMyKpis.month.total = parseFloat(updatedMyKpis.month.total) + pts;
            } else {
              // For historical entries, update only the appropriate period
              const dateRef = moment(month);
              const week = companyToday().startOf("week");
              const monthStart = companyToday().startOf("month");
              
              if (dateRef >= week) {
                if (!updatedMyKpis.week[kpiId]) {
                  updatedMyKpis.week[kpiId] = { pts: 0, amt: 0 };
                }
                updatedMyKpis.week[kpiId].pts = parseFloat(updatedMyKpis.week[kpiId].pts) + pts;
                updatedMyKpis.week[kpiId].amt = parseFloat(updatedMyKpis.week[kpiId].amt) + amt;
                updatedMyKpis.week.total = parseFloat(updatedMyKpis.week.total) + pts;
              }
              
              if (dateRef >= monthStart) {
                if (!updatedMyKpis.month[kpiId]) {
                  updatedMyKpis.month[kpiId] = { pts: 0, amt: 0 };
                }
                updatedMyKpis.month[kpiId].pts = parseFloat(updatedMyKpis.month[kpiId].pts) + pts;
                updatedMyKpis.month[kpiId].amt = parseFloat(updatedMyKpis.month[kpiId].amt) + amt;
                updatedMyKpis.month.total = parseFloat(updatedMyKpis.month.total) + pts;
              }
            }
            // Update the Redux store with the new KPI data
            dispatch({
              type: types.UPDATE_MY_KPIS,
              myKpis: updatedMyKpis
            });
          // }
        }
        
        console.log('✅ Offline KPI submission queued');
        return;
      } catch (offlineErr) {
        setError('Failed to store offline data: ' + offlineErr.message);
        throw offlineErr;
      }
    }
    // Online flow
    try {
      const result = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
        entryId,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }


      // Always patch Scorekeeper month totals + composites from the add response.
      // Previously this lived behind `myKpis` and skipped for some admin sessions,
      // so Revenue Credit could refresh while Test Composite stayed stale until reload.
      if (result.data && Array.isArray(monthRef)) {
        const pts = result.data.points || 0;
        const amt = result.data.amount || 0;
        const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
        // Create a user row if this player has no scores yet — otherwise the save
        // lands server-side but the Scorekeeper cell never moves (looks like "no save").
        let userData = updatedMonthRef.find(
          (user) => String(user.userId) === String(userId),
        );
        if (!userData) {
          userData = createEmptyUserKpiSummary(userId);
          updatedMonthRef.push(userData);
        }
        const srcKey = resolveMonthKpiKey(userData.month, kpiId) || String(kpiId);
        if (!userData.month[srcKey]) {
          userData.month[srcKey] = { pts, amt };
          userData.month.total = parseFloat((userData.month.total || 0) + pts);
        } else {
          userData.month[srcKey].pts = parseFloat(userData.month[srcKey].pts || 0) + pts;
          userData.month[srcKey].amt = parseFloat(userData.month[srcKey].amt || 0) + amt;
          userData.month.total = parseFloat(userData.month.total || 0) + pts;
        }

        const compositeScores = Array.isArray(result.data.composites)
          ? result.data.composites
          : [];
        compositeScores.forEach((row) => {
          if (!row?.kpiId) return;
          const ck =
            resolveMonthKpiKey(userData.month, row.kpiId) || String(row.kpiId);
          if (!userData.month[ck]) userData.month[ck] = { pts: 0, amt: 0 };
          userData.month[ck].amt = Number(row.amount) || 0;
          userData.month[ck].pts = Number(row.points) || 0;
        });

        dispatch({
          type: types.UPDATE_MONTH_KPIS,
          usersKpis: updatedMonthRef,
        });
      }

      // Update myKpis in the local state to reflect the new score
      if (result.data && myKpis) {
        const today = moment().startOf("day");
        const isToday = !month || moment(month).isSame(today, 'day');
        
        // Create a copy of the current KPI data
        const updatedMyKpis = { ...myKpis };

        let pts = 0;
        let amt = 0;
        // if (isAdmin && adminId === userId) {
        //   // Calculate points based on the KPI model
        //   pts = result.data.points / totalUsersInActiveWorkspace || 0;
        //   amt = result.data.amount / totalUsersInActiveWorkspace || 0;
        // }else{
        //   // Calculate points based on the KPI model
        //   pts = result.data.points || 0;
        //   amt = result.data.amount || 0;
        // }

        pts = result.data.points || 0;
        amt = result.data.amount || 0;

        // // Update adminKpis if user is admin
        // if (isAdmin) {
        //     const updatedAdminKpis = { ...myKpis };
        //     if (!updatedAdminKpis.month[kpiId]) {
        //       updatedAdminKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
        //       updatedAdminKpis.month.total += result.data.points;
        //       dispatch({
        //         type: types.UPDATE_ADMIN_KPIS,
        //         adminKpis: updatedAdminKpis
        //       });
        //     }else{
        //       updatedAdminKpis.month[kpiId].pts += pts;
        //       updatedAdminKpis.month[kpiId].amt += amt;
        //       updatedAdminKpis.month.total += pts;
        //       dispatch({
        //         type: types.UPDATE_ADMIN_KPIS,
        //         adminKpis: updatedAdminKpis
        //       });
        //     }
        // }else{
                  // Update the appropriate time period
          if (isToday) {
            // Update today's scores
            if (!updatedMyKpis.today[kpiId]) {
              updatedMyKpis.today[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.today.total += result.data.points;
            }else{
              updatedMyKpis.today[kpiId].pts += pts;
              updatedMyKpis.today[kpiId].amt += amt;
              updatedMyKpis.today.total += pts;
            }
            
            // Also update week and month
            if (!updatedMyKpis.week[kpiId]) {
              updatedMyKpis.week[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.week.total += result.data.points;
            }else{
              updatedMyKpis.week[kpiId].pts += pts;
              updatedMyKpis.week[kpiId].amt += amt;
              updatedMyKpis.week.total += pts;
            }
            
            if (!updatedMyKpis.month[kpiId]) {
              updatedMyKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.month.total += result.data.points;
            }else{
              updatedMyKpis.month[kpiId].pts += pts;
              updatedMyKpis.month[kpiId].amt += amt;
              updatedMyKpis.month.total += pts;
            }
          } else {
            // For historical entries, update only the appropriate period
            const dateRef = moment(month);
            const week = companyToday().startOf("week");
            const monthStart = companyToday().startOf("month");
            
            if (dateRef >= week) {
              if (!updatedMyKpis.week[kpiId]) {
                updatedMyKpis.week[kpiId] = { pts: result.data.points, amt: result.data.amount };
                updatedMyKpis.week.total += result.data.points;
              }else{
                updatedMyKpis.week[kpiId].pts += pts;
                updatedMyKpis.week[kpiId].amt += amt;
                updatedMyKpis.week.total += pts;
              }
            }
            
            if (dateRef >= monthStart) {
              if (!updatedMyKpis.month[kpiId]) {
                updatedMyKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
                updatedMyKpis.month.total += result.data.points;
              }else{
                updatedMyKpis.month[kpiId].pts += pts;
                updatedMyKpis.month[kpiId].amt += amt;
                updatedMyKpis.month.total += pts;
              }
            }
          }
          
          // Update the Redux store with the new KPI data
          dispatch({
            type: types.UPDATE_MY_KPIS,
            myKpis: updatedMyKpis
          });
        // }
      }

      // Soft refresh — composites already applied above; avoid locking Scorekeeper.
      dispatch(refreshKpis({ isLoading: false }));
      if (monthFormatted && monthFormatted !== companyMonthStr()) {
        dispatch(getPreviousKpisAction(month, true));
      }

    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    submitKpi,
    isSubmitting,
    error,
  };
};

export const useOfflineKpiSubmit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitOfflineKpi = async (kpiId, userId, month, data, companyId, id, entryId) => {
    setError(null);
    const monthFormatted = month ? moment(month).format("YYYY-MM") : null;
    setIsSubmitting(true);
    // Online flow
    try {
      const result = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
        entryId
      });

      if (result.data) {
        await deletePendingKpi(id);
        console.log('✅ Offline KPI deleted', id);
      }
      
      if (result.error) {
        throw new Error(result.error.message);
      }

    } catch (err) {
      if (err === "Request failed with status code 400") {
        await deletePendingKpi(id);
        console.log('✅ Offline KPI deleted - entry already existed2', id);
      }
      console.log('Error submitting offline kpi:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitOfflineKpi,
    isSubmitting,
    error,
  };
};

/**
 * Hook for updating total score.
 * Optional `month` (moment/Date/YYYY-MM): when it is a past month, updates
 * historicKpis only and never calls refreshKpis() (which would blank Scorekeeper).
 */
export const useTotalScoreSave = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const monthRef = useSelector(state => state.kpiReducer.usersKpis);
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const workspace = UseSelectedWorkspace();

  const saveTotalScore = async (kpiId, userId, amount, month = null) => {
    setIsSaving(true);
    setError(null);

    const monthFormatted = month ? moment(month).format("YYYY-MM") : null;
    const isPastMonth = !!monthFormatted && monthFormatted < companyMonthStr();

    const editedKpi = (groupKpis || []).find((k) => String(k?._id) === String(kpiId));
    const isHoursWorked = editedKpi?.model?.type === "hours_worked"
      || editedKpi?.type === "hours_worked";

    // Snapshot for rollback on error
    const prevMonthRef = monthRef;

    // Optimistic update for the source KPI only — fire immediately so the
    // edited cell reflects the new value without waiting for the server.
    // Skip for Hours Worked: set-total must finish (and return composites)
    // before we paint, otherwise a slow response leaves Hours at the typed
    // target while Composite still reflects the old/inflated denominator.
    // Composite cells are intentionally excluded here; Table.jsx marks them
    // as pending (spinner) and waits for the server refresh to show the real value.
    if (!isPastMonth && !isHoursWorked && Array.isArray(monthRef)) {
      const optimistic = JSON.parse(JSON.stringify(monthRef));
      let userData = optimistic.find((u) => String(u.userId) === String(userId));
      if (!userData) {
        userData = createEmptyUserKpiSummary(userId);
        optimistic.push(userData);
      }
      const srcKey = String(kpiId);
      if (!userData.month[srcKey]) userData.month[srcKey] = { pts: 0, amt: 0 };
      userData.month[srcKey].amt = amount;
      dispatch({ type: types.UPDATE_MONTH_KPIS, usersKpis: optimistic });
    }

    try {
      const result = await UPDATE_TOTAL_SCORE(
        kpiId,
        userId,
        amount,
        isPastMonth ? monthFormatted : null,
      );

      if (result.error) {
        throw new Error(toErrorMessage(result.error, "Could not save score"));
      }

      const saved = result.data;
      const finalPoints = saved?.points ?? 0;
      let finalAmount = amount;
      if (Number.isFinite(Number(saved?.amount))) {
        finalAmount = Number(saved.amount);
      } else if (Number.isFinite(Number(saved?.target))) {
        finalAmount = Number(saved.target);
      }

      if (isPastMonth) {
        dispatch({
          type: types.SET_PAST_MONTH_KPI_TOTAL_SUCCESS,
          monthKey: monthFormatted,
          userId,
          kpiId,
          amount: finalAmount,
          points: finalPoints,
        });
        // Background refetch keeps TanStack + Redux aligned; never refreshKpis.
        if (workspace?._id) {
          queryClient.invalidateQueries(
            pastMonthKeys.scores(monthFormatted, workspace._id),
          );
        }
        return saved;
      }

      if (!Array.isArray(monthRef)) {
        dispatch(refreshKpis({ isLoading: false }));
        return saved;
      }

      // Reconcile actual server points into the already-dispatched optimistic state.
      const kpiById = {};
      (groupKpis || []).forEach((k) => {
        if (k?._id) kpiById[k._id] = k;
      });

      const reconciled = JSON.parse(JSON.stringify(monthRef));
      let userData = reconciled.find((u) => String(u.userId) === String(userId));
      if (!userData) {
        userData = createEmptyUserKpiSummary(userId);
        reconciled.push(userData);
      }

      const srcKey = String(kpiId);
      if (!userData.month[srcKey]) userData.month[srcKey] = { pts: 0, amt: 0 };
      userData.month[srcKey].amt = finalAmount;
      userData.month[srcKey].pts = finalPoints;

      // Apply server-recomputed composites immediately so Scorekeeper does not
      // wait on a hung full KPI refresh to drop the Recomputing spinner.
      const compositeScores = Array.isArray(saved?.composites) ? saved.composites : [];
      compositeScores.forEach((row) => {
        if (!row?.kpiId) return;
        const ck =
          resolveMonthKpiKey(userData.month, row.kpiId) || String(row.kpiId);
        if (!userData.month[ck]) userData.month[ck] = { pts: 0, amt: 0 };
        userData.month[ck].amt = Number(row.amount) || 0;
        userData.month[ck].pts = Number(row.points) || 0;
      });

      let recomputedTotal = 0;
      Object.keys(userData.month).forEach((key) => {
        if (key === "total") return;
        const row = userData.month[key];
        const kpi = findKpiInMap(kpiById, key);
        if (kpi?.metricType === "anchor") {
          recomputedTotal += Number(row?.pts) || 0;
        } else if (kpi) {
          const scale = Number(kpi.pointScale) || 0;
          const value = Number(kpi.pointValue) || 0;
          const ptsPerAmt = scale > 0 ? value / scale : value;
          recomputedTotal += (Number(row?.amt) || 0) * ptsPerAmt;
        } else {
          recomputedTotal += Number(row?.pts) || 0;
        }
      });
      userData.month.total = recomputedTotal;

      dispatch({ type: types.UPDATE_MONTH_KPIS, usersKpis: reconciled });
      // Soft refresh: keep isLoading false so composite cells are not stuck on
      // Recomputing if GetKpis is slow/hung after the save already succeeded.
      dispatch(refreshKpis({ isLoading: false }));

      return saved;
    } catch (err) {
      // Roll back the optimistic update so the UI shows the original value.
      if (!isPastMonth && Array.isArray(prevMonthRef)) {
        dispatch({ type: types.UPDATE_MONTH_KPIS, usersKpis: prevMonthRef });
      }
      const message = toErrorMessage(err, "Could not save score");
      setError(message);
      throw new Error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveTotalScore,
    isSaving,
    error,
  };
};

/**
 * Hook for updating KPI records from timeline
 */
export const useKpiUpdateScore = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const monthRef = useSelector(state => state.kpiReducer.usersKpis);

  const updateKpiScore = async (kpiId, userId, oldAmount, oldPoints, newAmount, newPoints) => {
    setIsUpdating(true);
    setError(null);

    try {
        setError(null);
        // Update the monthly scores
        const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
        const userData = updatedMonthRef.find(user => user.userId === userId);
        if (userData) {
          if (!userData.month[kpiId]) {
            userData.month[kpiId] = { pts: 0, amt: 0 };
          }
          // Remove old values
          userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) - parseFloat(oldPoints || 0);
          userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) - parseFloat(oldAmount || 0);
          
          // Add new values 
          userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) + parseFloat(newPoints || 0);
          userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) + parseFloat(newAmount || 0);
          
          // Update total points
          userData.month.total = parseFloat(userData.month.total || 0) - parseFloat(oldPoints || 0);
          userData.month.total = parseFloat(userData.month.total || 0) + parseFloat(newPoints || 0);
        }
        dispatch({
          type: types.UPDATE_MONTH_KPIS,
          usersKpis: updatedMonthRef
        });
        dispatch(refreshKpis());
      // if (result.error) {
      //   throw new Error(result.error.message);
      // }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateKpiScore,
    isUpdating,
    error,
  };
};

/**
 * Hook for updating KPI records
 */
export const useKpiUpdate = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateKpi = async (id, kpiId, amount) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_USER_KPI(id, kpiId, amount);

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateKpi,
    isUpdating,
    error,
  };
};

/**
 * Hook for deleting KPI records
 */
export const useKpiDelete = () => {
  const { refetch } = useKpiData();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const deleteKpi = async (kpiId) => {
    setIsDeleting(true);
    setError(null);

    try {
      const result = await DELETE_USER_KPI(kpiId);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteKpi,
    isDeleting,
    error,
  };
};

/**
 * Hook for updating workspace KPIs
 */
export const useWorkspaceKpiUpdate = () => {
  const { refetch } = useKpiData();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateWorkspaceKpi = async (groupId, kpiObj) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_GROUP_KPI(groupId, kpiObj);

      if (result.error) {
        throw new Error(result.error.message);
      }

      // Don't make the caller (and the save-confirmation UI that follows)
      // wait on the full board refetch — same optimistic pattern
      // CompanyMetrics/index.jsx already uses for its appearance save.
      // The KPI is already saved server-side; the board reconciles in the
      // background as soon as this resolves.
      refetch().catch(() => {});
      return result?.data ?? result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateKpiGuideText = async (kpiId, guideText) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_KPI_GUIDE_TEXT(kpiId, guideText);

      if (result.error) {
        throw new Error(result.error.message);
      }

      refetch().catch(() => {});
      return result?.data ?? result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateKpiDailyGoalText = async (kpiId, dailyGoalText) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_KPI_DAILY_GOAL_TEXT(kpiId, dailyGoalText);

      if (result.error) {
        throw new Error(result.error.message);
      }

      refetch().catch(() => {});
      return result?.data ?? result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateWorkspaceKpi,
    updateKpiGuideText,
    updateKpiDailyGoalText,
    isUpdating,
    error,
  };
};

/**
 * Hook for creating custom KPIs
 */
export const useCustomKpiCreate = () => {
  const { refetch } = useKpiData();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const createCustomKpi = async (kpiData) => {
    setIsCreating(true);
    setError(null);

    try {
      const result = await CREATE_CUSTOM_KPI(kpiData);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createCustomKpi,
    isCreating,
    error,
  };
};

/**
 * Hook for disabling workspace KPIs
 */
export const useWorkspaceKpiDisable = () => {
  const { refetch } = useKpiData();
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState(null);

  const disableWorkspaceKpi = async (kpiId) => {
    setIsDisabling(true);
    setError(null);

    try {
      const result = await DISABLE_GROUP_KPI(kpiId);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsDisabling(false);
    }
  };

  return {
    disableWorkspaceKpi,
    isDisabling,
    error,
  };
};

/**
 * Hook for getting previous KPIs
 */
export const useGetPreviousKpis = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selectors for state
  const prevKpis = useSelector((state) => state.kpiReducer.historicKpis);
  const monthRef = useSelector((state) => state.kpiReducer.monthRef);
  const { companyId } = UseUserDetails();
  const workspace = UseSelectedWorkspace();

  const getPreviousKpis = useCallback(
    async (month, refresh = false) => {
      setIsLoading(true);
      setError(null);

      let dateRef;
      if (!month) {
        dateRef = monthRef;
      } else {
        dateRef = moment(month).format("YYYY-MM");
      }

      try {
        if (!refresh && prevKpis?.[dateRef]) {
          dispatch({
            type: types.SET_TO_HISTORIC_KPIS,
            monthRef: dateRef,
          });
        } else {
          const scoreKpis = await GET_ALL_USER_KPIS(
            companyId,
            workspace?._id,
            dateRef
          );

          const userScores = [];
          const sharedScores = [];

          scoreKpis.data.scores.forEach((score) => {
            if (score.scope === "User") {
              userScores.push(score);
            } else {
              sharedScores.push(...score.days);
            }
          });

          if (sharedScores.length) {
            scoreKpis.data.users.forEach((user) => {
              const idx = userScores.findIndex((u) => u.ownerId === user);
              if (idx >= 0) {
                userScores[idx].days.push(...sharedScores);
              } else {
                userScores.push({
                  ownerId: user,
                  days: [...sharedScores],
                });
              }
            });
          }

          const usersKpis = userScores.map((userData) => {
            const summary = {
              userId: userData.ownerId,
              month: { total: 0 },
            };

            for (let d = 0; d < userData.days.length; d++) {
              const dayData = userData.days[d];
              addKpiObjects(summary.month, dayData.kpis);
            }

            return summary;
          });

          const newKpiHistory = {};
          newKpiHistory[dateRef] = usersKpis.sort(
            (a, b) => b.month.total - a.month.total
          );

          dispatch({
            type: types.GET_PREVIOUS_KPIS_SUCCESS,
            historicKpis: newKpiHistory,
            monthRef: dateRef,
          });
        }
      } catch (err) {
        setError(err.message);
        dispatch({ type: types.GET_PREVIOUS_KPIS_FAILED, error: err.message });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, prevKpis, companyId, workspace, monthRef]
  );

  return {
    getPreviousKpis,
    isLoading,
    error,
    monthRef,
  };
};

/**
 * Hook for submitting KPI records for a past month.
 * Structurally separate from useKpiSubmit — never touches usersKpis (current month),
 * never calls refreshKpis(). Dispatches ADD_PAST_MONTH_KPI_SUCCESS which merges
 * into historicKpis[monthKey] only, then re-fetches that month from the server.
 */
export const usePastMonthKpiSubmit = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitPastMonthKpi = async (kpiId, userId, month, data, companyId) => {
    // Fresh id per SAVE (see useKpiSubmit) — the entry_dedup unique index
    // rejects reused entryIds, failing repeat saves from the same render.
    const entryId = generateUuid();
    setIsSubmitting(true);
    setError(null);
    const monthFormatted = moment(month).format("YYYY-MM");

    try {
      const result = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
        entryId,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      const { amount, points } = result.data;

      dispatch({
        type: types.ADD_PAST_MONTH_KPI_SUCCESS,
        monthKey: monthFormatted,
        userId,
        kpiId,
        amount,
        points,
      });
      // Re-fetch the month from the server (refresh=true bypasses the cache).
      // Safe because: (1) generateKpiScoreSummary is awaited before the 201
      // response, so the data is accurate; (2) ManualEntryTable uses local
      // isLoading, not Redux's, so the table won't blank; (3) GET_PREVIOUS_KPIS_SUCCESS
      // merges into historicKpis rather than overwriting it.
      dispatch(getPreviousKpisAction(monthFormatted, true));

    } catch (err) {
      const message = toErrorMessage(err, "Could not save score");
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitPastMonthKpi, isSubmitting, error };
};